import React, { useState, useEffect, useRef } from 'react';
import { QrCode, Search, CheckCircle, XCircle, AlertCircle, User, Calendar, MapPin, RefreshCw, Camera } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckInTab = ({ token }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [lastCheckin, setLastCheckin] = useState(null);
  const [checkinStats, setCheckinStats] = useState({ total: 0, checkedIn: 0 });
  const inputRef = useRef(null);

  useEffect(() => {
    fetchEvents();
    // Focus on search input
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventStats();
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/events?status=published`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const today = new Date().toISOString().split('T')[0];
      // Filter to today's events or upcoming
      const relevantEvents = response.data.items?.filter(e => e.date >= today) || [];
      setEvents(relevantEvents);
      
      // Auto-select today's event if there is one
      const todayEvent = relevantEvents.find(e => e.date === today);
      if (todayEvent) {
        setSelectedEvent(todayEvent.event_id);
      }
    } catch (error) {
      console.error('Error fetching events');
    }
  };

  const fetchEventStats = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/events/${selectedEvent}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckinStats({
        total: response.data.stats?.total_tickets || 0,
        checkedIn: response.data.stats?.checked_in || 0
      });
    } catch (error) {
      console.error('Error fetching stats');
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: searchTerm });
      if (selectedEvent) params.append('event_id', selectedEvent);
      
      const response = await axios.get(`${API}/api/admin/checkin/search?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      toast.error('Error en la búsqueda');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async (ticketId, qrData = '') => {
    try {
      const response = await axios.post(`${API}/api/admin/checkin/validate`, 
        { ticket_id: ticketId, qr_data: qrData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLastCheckin(response.data);
      
      if (response.data.valid) {
        toast.success('✅ Check-in exitoso');
        // Update local state
        setSearchResults(prev => prev.map(t => 
          t.ticket_id === ticketId ? { ...t, checked_in: true } : t
        ));
        fetchEventStats();
      } else {
        toast.error(response.data.message);
      }
      
      // Clear search after checkin
      setTimeout(() => {
        setSearchTerm('');
        setSearchResults([]);
        inputRef.current?.focus();
      }, 2000);
      
    } catch (error) {
      toast.error('Error al procesar check-in');
    }
  };

  const handleQRInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // If it looks like a QR code (contains GSAGENCY|), process immediately
    if (value.includes('GSAGENCY|')) {
      const parts = value.split('|');
      if (parts.length >= 2) {
        handleCheckin(parts[1], value);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <QrCode className="w-6 h-6 mr-2 text-amber-500" />
            Check-in
          </h2>
          <p className="text-gray-400 text-sm">Validación de accesos con QR</p>
        </div>
        
        {/* Stats */}
        {selectedEvent && (
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{checkinStats.checkedIn}</p>
              <p className="text-gray-400 text-sm">Check-ins</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-400">{checkinStats.total - checkinStats.checkedIn}</p>
              <p className="text-gray-400 text-sm">Pendientes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">
                {checkinStats.total > 0 ? ((checkinStats.checkedIn / checkinStats.total) * 100).toFixed(0) : 0}%
              </p>
              <p className="text-gray-400 text-sm">Asistencia</p>
            </div>
          </div>
        )}
      </div>

      {/* Event Selector */}
      <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
        <label className="block text-gray-400 text-sm mb-2">Evento activo</label>
        <select
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          className="w-full bg-zinc-800 text-white px-4 py-3 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none text-lg"
        >
          <option value="">Seleccionar evento...</option>
          {events.map(event => (
            <option key={event.event_id} value={event.event_id}>
              {event.name?.es} - {event.date}
            </option>
          ))}
        </select>
      </div>

      {/* Scanner Input */}
      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-2xl p-6 border border-amber-500/30">
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center">
            <Camera className="w-8 h-8 text-amber-500" />
          </div>
        </div>
        <p className="text-center text-gray-300 mb-4">Escanea el código QR o busca por nombre/email/ticket</p>
        
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <QrCode className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleQRInput}
              placeholder="Escanear QR o buscar..."
              className="w-full bg-zinc-900 text-white pl-12 pr-4 py-4 rounded-xl border border-zinc-700 focus:border-amber-500 focus:outline-none text-lg"
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-4 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 disabled:opacity-50"
          >
            {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          </button>
        </form>
      </div>

      {/* Last Checkin Result */}
      {lastCheckin && (
        <div className={`rounded-xl p-6 border ${
          lastCheckin.valid 
            ? 'bg-green-500/10 border-green-500/30' 
            : lastCheckin.status === 'already_used'
              ? 'bg-yellow-500/10 border-yellow-500/30'
              : 'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="flex items-center justify-center mb-4">
            {lastCheckin.valid ? (
              <CheckCircle className="w-16 h-16 text-green-500" />
            ) : lastCheckin.status === 'already_used' ? (
              <AlertCircle className="w-16 h-16 text-yellow-500" />
            ) : (
              <XCircle className="w-16 h-16 text-red-500" />
            )}
          </div>
          <h3 className={`text-2xl font-bold text-center mb-2 ${
            lastCheckin.valid ? 'text-green-400' : lastCheckin.status === 'already_used' ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {lastCheckin.message}
          </h3>
          {lastCheckin.ticket && (
            <div className="mt-4 bg-black/20 rounded-lg p-4 text-center">
              <p className="text-white font-medium text-lg">{lastCheckin.ticket.holder_name || 'Sin nombre'}</p>
              <p className="text-gray-400">{lastCheckin.ticket.access_level}</p>
              <p className="text-gray-500 text-sm font-mono">{lastCheckin.ticket.ticket_id}</p>
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">Resultados ({searchResults.length})</h3>
          {searchResults.map((result) => (
            <div 
              key={result.ticket_id}
              className={`bg-zinc-900 rounded-xl border p-4 ${
                result.checked_in ? 'border-green-500/30' : 'border-zinc-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {result.qr_code && (
                    <img 
                      src={`data:image/png;base64,${result.qr_code}`}
                      alt="QR"
                      className="w-16 h-16 bg-white rounded-lg p-1"
                    />
                  )}
                  <div>
                    <p className="text-white font-medium">{result.customer_name}</p>
                    <p className="text-gray-400 text-sm">{result.customer_email}</p>
                    <p className="text-gray-500 text-xs">
                      {result.access_level_name} • Ticket #{result.attendee_number}
                    </p>
                    <p className="text-gray-600 text-xs font-mono">{result.ticket_id}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  {result.checked_in ? (
                    <div className="flex items-center text-green-400">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      <span>Ya ingresó</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleCheckin(result.ticket_id)}
                      className="px-4 py-2 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400"
                    >
                      Check-in
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
        <h3 className="text-white font-medium mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2 text-amber-500" />
          Instrucciones
        </h3>
        <ul className="space-y-2 text-gray-400 text-sm">
          <li>• Conecta un lector de código QR a tu computadora</li>
          <li>• El campo de búsqueda detectará automáticamente los códigos QR</li>
          <li>• También puedes buscar por nombre, email o número de ticket</li>
          <li>• Verifica que el asistente presente identificación oficial</li>
          <li>• Un ticket solo puede usarse una vez</li>
        </ul>
      </div>
    </div>
  );
};

export default CheckInTab;
